<template>
  <div
    :id="'bar-chart-horizontal-container-' + dataName"
    class="bar-chart-horizontal-container d-flex justify-content-end"
  >
    <div
      :id="'bar-chart-horizontal-' + dataName"
      ref="chart"
      class="bar-chart-horizontal"
    ></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { primaryColor } from '@/puls-color-theme'

export default {
  name: 'BarChartHorizontal',
  props: {
    data: {
      type: Array,
      required: true
    },
    dataName: {
      type: String,
      default: ''
    },
    options: {
      type: Object,
      default: () => {}
    },
    theme: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      svg: null,
      margin: {
        top: 10,
        right: 0,
        bottom: 60,
        left: 20
      },
      labelWidth: 440,
      valueGutter: 36,
      transitionTime: 350,
      line: null,
      x: null,
      y: null
    }
  },
  computed: {
    dataReady() {
      return this.data
    },
    domain() {
      // defaults so that the highest bar is always at top
      const max = d3.max(this.dataReady, (d) => {
        return d.value
      })
      if (max === 0) {
        return [0, 10] // fallback if all are empty
      } else {
        return this.options && this.options.domain
          ? this.options.domain
          : [
              0,
              d3.max(this.dataReady, (d) => {
                return d.value
              })
            ]
      }
    },
    height() {
      const defaultHeight = this.options.height
        ? this.options.height - this.margin.top - this.margin.bottom
        : 380 // 280
      return defaultHeight - this.margin.top - this.margin.bottom
    },
    ticks() {
      const max = d3.max(this.dataReady, (d) => {
        return d.value
      })
      let ticks = 10 // default
      if (max > 10) {
        ticks = 4
      }
      if (max < 10) {
        ticks = max
      }
      return this.options && this.options.ticks ? this.options.ticks : ticks
    },
    width() {
      const defaultWidth = this.options.width
        ? this.options.width - this.margin.left - this.margin.right
        : 640
      return defaultWidth - this.margin.left - this.margin.right
    }
  },
  mounted() {
    this.initChart()
    this.updateChart()
  },
  methods: {
    addMouseTooltip(element) {
      element
        .on('mouseover', this.onMouseOver)
        .on('mousemove', this.onMouseMove)
        .on('mouseleave', this.onMouseLeave)
    },
    colorForBarChartValue(choiceNumber) {
      if (!Number.isInteger(choiceNumber)) {
        return primaryColor
      } else {
        const maxForRed = this.dataName.includes('enps') ? 7 : 4
        const minOrEqualForGreen = this.dataName.includes('enps') ? 9 : 7
        if (this.theme === 'monochrome' || choiceNumber === null) {
          return this.$resultColors.brand
        } else {
          if (choiceNumber < maxForRed) {
            return this.$resultColors.red
          } else if (choiceNumber >= minOrEqualForGreen) {
            return this.$resultColors.green
          } else {
            return this.$resultColors.yellow
          }
        }
      }
    },
    initChart() {
      // tooltip
      d3.select('body')
        .append('div')
        .attr(
          'class',
          `tooltip tooltip-bar-chart-horizontal-container-${this.dataName}`
        )
        .style('opacity', 0.0)
        .style('pointer-events', 'none')
        .style('min-width', '300px;')
        .style('z-index', 4000)
    },
    updateChart() {
      const that = this

      const width = this.width - this.margin.left - this.margin.right
      const height = this.height - this.margin.top - this.margin.bottom

      const svg = d3
        .select(this.$refs.chart)
        .append('svg')
        .attr('preserveAspectRatio', 'xMaxYMax slice')
        .attr(
          'viewBox',
          `0 0 ${this.width + this.margin.left + this.margin.right} ${
            this.height + this.margin.top + this.margin.bottom
          }`
        )
        .classed('svg-content-responsive', true)
        // .attr('width', width + this.margin.left + this.margin.right)
        // .attr('height', height + this.margin.top + this.margin.bottom)
        .append('g')
        .attr(
          'transform',
          'translate(' +
            (this.labelWidth + this.margin.left) +
            ',' +
            this.margin.top +
            ')'
        )

      this.svg = svg
      const chartMax = width - this.labelWidth - this.valueGutter

      const x = d3
        .scaleLinear()
        .domain([0, d3.max(this.data, (d) => +d.value) || 1])
        .range([0, chartMax])

      // Grid
      var xGrid = d3
        .scaleLinear()
        .domain([0, d3.max(this.data, (d) => +d.value) || 1])
        .range([0, chartMax])

      var xAxis = d3
        .axisTop(xGrid)
        .ticks(this.ticks)
        .tickSize(-height)
        .tickSizeOuter(0)
        .tickFormat('')

      svg.append('g').attr('class', 'barchart-grid').call(xAxis)

      // Add X axis
      svg
        .append('g')
        .attr('class', 'x barchart-axis')
        .attr('transform', `translate(0, ${height})`)
        .call(d3.axisBottom(x).ticks(this.ticks).tickFormat(d3.format('d')))
        .selectAll('text')
        .attr('transform', 'translate(3,0)')
        .style('text-anchor', 'end')

      // Y scale
      const y = d3
        .scaleBand()
        .range([0, height])
        .domain(this.data.map((d) => d.name))
        .padding(0.4)

      const yAxisGroup = svg
        .append('g')
        .attr('class', 'y barchart-axis')
        .call(d3.axisLeft(y))

      const maxLabelWidth = this.labelWidth - 15

      yAxisGroup
        .selectAll('.tick text')
        .style('text-anchor', 'start')
        .attr('x', -this.labelWidth + 5)
        .attr('class', `bar-chart-horizontal-label`)
        .style('cursor', 'pointer')
        .style('dominant-baseline', 'middle')
        .attr('x', -this.labelWidth + 10)
        .each(function (d) {
          const text = d3.select(this)
          const fullText = d

          text.text(fullText)
          let t = fullText
          let textLength = this.getComputedTextLength()
          while (textLength > maxLabelWidth && t.length > 0) {
            t = t.slice(0, -1)
            text.text(t + '…')
            textLength = this.getComputedTextLength()
          }
          if (t !== fullText) {
            text.append('title').text(fullText)
          }
        })

      // Add tooltips to Y-axis labels
      yAxisGroup.selectAll('.tick text').each(function (label) {
        const dataForLabel = that.data.find((d) => d.name === label)
        var value = dataForLabel ? `${dataForLabel.value}` : '-'
        if (dataForLabel.value === 0) {
          d3.select(this).attr(
            'class',
            'bar-chart-horizontal-label bar-chart-horizontal-label-muted'
          )
        }

        if (value !== '-' && dataForLabel.label) {
          value += ` ${that.$t('categoryDetails.tooltip.barValueCondensed', {
            label: dataForLabel.label
          })}`
        }

        d3.select(this)
          .style('cursor', 'pointer')
          .on('mouseover', () => {
            svg
              .selectAll(`.bar-rect-${that.dataName}`)
              .classed('is-active', (d) => d.name === label)
              .classed('is-faded', (d) => d.name !== label)

            d3.select(this).classed('is-active', true)
            const tooltip = d3.select(
              `.tooltip-bar-chart-horizontal-container-${that.dataName}`
            )
            tooltip.transition().duration(200).style('opacity', 0.95)
            tooltip.html(
              `<div class="p-2 tooltip-bg fs-5"><p class="mt-2 fw-bold">${label}</p><p class="mb-1"> ${value}</p></div>`
            )
          })
          .on('mousemove', that.onMouseMove)
          .on('mouseleave', function () {
            svg
              .selectAll(`.bar-rect-${that.dataName}`)
              .classed('is-active', false)
              .classed('is-faded', false)

            d3.select(this).classed('is-active', false)
            const tooltip = d3.select(
              `.tooltip-bar-chart-horizontal-container-${that.dataName}`
            )
            tooltip.transition().duration(200).style('opacity', 0)
          })
      })

      const maxBarHeight = 30

      // Bars
      svg
        .selectAll('rect')
        .data(this.data)
        .enter()
        .append('rect')
        .attr('class', `bar bar-rect-${that.dataName}`)
        .attr('data-name', (d) => d.name)
        .attr('x', x(0))
        .attr('y', (d) => {
          const barH = Math.min(y.bandwidth(), maxBarHeight)
          return y(d.name) + (y.bandwidth() - barH) / 2
        })
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('width', (d) => x(d.value))
        .attr('height', () => Math.min(y.bandwidth(), maxBarHeight))
        .attr('fill', (d) => that.colorForBarChartValue(d.name))

      const valueFmt = d3.format('d')
      svg
        .selectAll('text.bar-value')
        .data(this.data)
        .enter()
        .append('text')
        .attr('class', `bar-value bar-value-${that.dataName}`)
        .attr('x', (d) => {
          const afterTip = x(d.value) + 3
          const maxX = width - this.labelWidth - 8
          return Math.min(afterTip, maxX)
        })
        .attr('y', (d) => {
          const barH = Math.min(y.bandwidth(), maxBarHeight)
          return y(d.name) + (y.bandwidth() - barH) / 2 + barH / 2
        })
        .style('font-size', '12px')
        .attr('dy', '0.35em')
        .style('text-anchor', 'start')
        .style('pointer-events', 'none')
        .text((d) => valueFmt(d.value))

      this.addMouseTooltip(d3.selectAll(`.bar-rect-${this.dataName}`))
    },
    onMouseOver(d, target) {
      const tooltip = d3.select(
        `.tooltip-bar-chart-horizontal-container-${this.dataName}`
      )
      tooltip.transition().duration(200).style('opacity', 0.95)

      // not stacked
      const percentageValue = Object.keys(target).includes('value')
        ? Math.round((target.value * 100) / this.numberOfResponses)
        : null

      const percentageString = isFinite(percentageValue)
        ? `${percentageValue}%`
        : ''

      var s = this.options.useOnlyPercentage
        ? percentageString
        : this.$t(
            this.options.denotingAnswers
              ? 'categoryDetails.tooltip.barValueAnswers'
              : 'categoryDetails.tooltip.barValue',
            target.value
          ) + (percentageString ? ` (${percentageString})` : '')

      if (target.label) {
        s += ` ${this.$t('categoryDetails.tooltip.barValueCondensed', {
          label: target.label
        })}`
      }

      const title = d.title ? d.title : ''
      tooltip.html(
        `<div class="p-2 tooltip-bg"><div><p class="tooltip-title">${title}</p></div><div><table><tr><td class="d-flex align-items-start"><i class="bi bi-people-fill fs-3 me-3"></i>${s}</td></tr></table></div></div>`
      )
    },
    onMouseMove(d) {
      const tooltip = document.getElementsByClassName(
        `tooltip-bar-chart-horizontal-container-${this.dataName}`
      )[0]
      const marginTop = 10
      const tooltipBox = tooltip.getBoundingClientRect()
      const tooltipOffsetX = -tooltipBox.width / 2 // half tooltip
      const tooltipOffsetY = -tooltipBox.height - marginTop
      const left = d.pageX + tooltipOffsetX
      const top = d.pageY + tooltipOffsetY
      tooltip.style.left = left + 'px'
      tooltip.style.top = top + 'px'
    },
    onMouseLeave(d) {
      d3.select(`.tooltip-bar-chart-horizontal-container-${this.dataName}`)
        .transition()
        .duration(this.transitionTime)
        .style('opacity', 0)
        .on('end', function () {
          d3.select(this).style('left', null).style('top', null)
        })
    }
  }
}
</script>

<style lang="scss">
.bar-chart-horizontal {
  display: inline-block;
  position: relative;
  width: 100%;
  padding-bottom: 36%;
  vertical-align: top;
  overflow: hidden;
}
.bar-chart-horizontal-container {
  width: 100%;
  overflow: visible;
  font-family: 'Roboto', sans-serif;
}
.tooltip-bg {
  background-color: var(--kt-input-bg) !important;
  border-radius: 5px;
  box-shadow: 2px 2px 2px var(--kt-table-border-color);
}
.barchart-grid > path {
  stroke: none;
  fill: none;
}
.barchart-grid > .tick > line {
  opacity: 0.7;
  stroke: lightgray;
}

.barchart-axis > .tick > line {
  stroke: lightgray;
  color: white;
}
.barchart-axis > .tick > text {
  color: var(--kt-headings-color);
  font-weight: 400;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
}

.barchart-axis > path {
  stroke: lightgray;
}
</style>

<style lang="scss">
.bar.is-active {
  stroke: var(--kt-primary, #2979ff);
  stroke-width: 2;
  filter: brightness(1.05);
}
.bar.is-faded {
  opacity: 0.35;
}
.bar-chart-horizontal-label {
  font-size: 16px;
  transition: color 0.1s ease;
  cursor: default !important;
  &.is-active {
    color: var(--kt-primary-active) !important;
  }
}

.bar-chart-horizontal-label-muted {
  color: var(--kt-secondary-active) !important;
}

.bar-value {
  fill: currentColor;
  color: var(--kt-headings-color);
}

@media (min-width: 768px) {
  .bar-chart-horizontal-label {
    font-size: 12px;
  }
}
</style>
